import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientVerificationApi } from '../api/clientVerificationApi';
import { CLIENTS_QUERY_KEY } from './useClients';

export type ArchiveStatus = 'idle' | 'waiting' | 'available' | 'downloaded' | 'reviewing' | 'ready_to_verify' | 'completed';

interface ArchiveState {
  status: ArchiveStatus;
  archiveUrl?: string;
  timeRemaining?: number;
  error?: string;
}

const POLLING_INTERVAL = 5000; // 5 seconds
const REVIEW_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

export function useArchivePolling(clientId: string, verificationStatus: string) {
  const queryClient = useQueryClient();

  // Only poll if client needs verification
  const shouldPoll = verificationStatus === 'verification_needed';

  // Use the same query key for both initial check and polling to leverage cache
  const archiveQueryKey = ['archive-url', clientId];

  // Check cache first to see if we already have the archive URL
  const cachedArchiveData = queryClient.getQueryData<{ url: string }>(archiveQueryKey);

  // Initialize state - if we have cached data, start with 'available', otherwise 'idle'
  const getInitialState = (): ArchiveState => {
    if (shouldPoll && cachedArchiveData?.url) {
      return { status: 'available', archiveUrl: cachedArchiveData.url };
    }
    return { status: 'idle' };
  };

  const [archiveState, setArchiveState] = useState<ArchiveState>(getInitialState);
  const [reviewStartTime, setReviewStartTime] = useState<number | null>(null);
  const [hasCheckedInitially, setHasCheckedInitially] = useState(!!cachedArchiveData?.url);

  // Initial check query - runs once to check if archive is already available
  // Use the same query key as polling to share cache
  const { data: initialArchiveData, error: initialError, isLoading: isInitialLoading } = useQuery({
    queryKey: archiveQueryKey,
    queryFn: () => clientVerificationApi.getDocumentsArchiveUrl(clientId),
    enabled: shouldPoll && archiveState.status === 'idle' && !hasCheckedInitially,
    retry: false,
    refetchOnWindowFocus: false,
    // Use cached data if available
    initialData: cachedArchiveData ? { url: cachedArchiveData.url } : undefined,
  });

  // Polling query - runs only if archive is not available yet
  const { data: archiveData, error } = useQuery({
    queryKey: archiveQueryKey,
    queryFn: () => clientVerificationApi.getDocumentsArchiveUrl(clientId),
    enabled: shouldPoll && archiveState.status === 'waiting',
    refetchInterval: POLLING_INTERVAL,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Mutations for accept/decline
  const acceptMutation = useMutation({
    mutationFn: () => clientVerificationApi.acceptArchiveVerification(clientId),
    onSuccess: () => {
      setArchiveState({ status: 'completed' });
      queryClient.invalidateQueries({ queryKey: [...CLIENTS_QUERY_KEY, clientId] });
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => clientVerificationApi.declineArchiveVerification(clientId),
    onSuccess: () => {
      setArchiveState({ status: 'completed' });
      queryClient.invalidateQueries({ queryKey: [...CLIENTS_QUERY_KEY, clientId] });
    },
  });

  // Handle initial archive check - only if we started with 'idle' state
  useEffect(() => {
    // If we already have archive from cache, skip this effect
    if (hasCheckedInitially && archiveState.status === 'available') {
      return;
    }

    if (shouldPoll && archiveState.status === 'idle' && !hasCheckedInitially) {
      // Wait for query to complete
      if (!isInitialLoading) {
        if (initialArchiveData?.url) {
          // Archive is available from query
          setArchiveState({
            status: 'available',
            archiveUrl: initialArchiveData.url,
          });
          setHasCheckedInitially(true);
        } else if (initialError) {
          // Check if it's a 400 error with no_archive message (expected)
          // This is the normal case when archive is not ready yet
          const errorStatus = (initialError as any)?.status;
          const errorDetail = (initialError as any)?.detail || initialError.message || '';
          const isNoArchiveError =
            errorStatus === 400 || // 400 status means archive not ready yet
            errorDetail.toLowerCase().includes('no_archive') ||
            errorDetail.toLowerCase().includes('archive not found') ||
            errorDetail.toLowerCase().includes('not ready');

          if (isNoArchiveError) {
            // Archive not ready yet, start polling - this is expected behavior
            setArchiveState({ status: 'waiting' });
            setHasCheckedInitially(true);
          } else {
            // Unexpected error (not 400, not no_archive related)
            setArchiveState({
              status: 'waiting',
              error: 'Failed to check archive status'
            });
            setHasCheckedInitially(true);
          }
        }
      }
    }
  }, [initialArchiveData, initialError, archiveState.status, shouldPoll, hasCheckedInitially, isInitialLoading]);

  // Handle archive availability during polling
  useEffect(() => {
    if (archiveData?.url && archiveState.status === 'waiting') {
      setArchiveState({
        status: 'available',
        archiveUrl: archiveData.url,
      });
    }
  }, [archiveData, archiveState.status]);

  // Handle API errors during polling (no_archive case)
  useEffect(() => {
    if (error && archiveState.status === 'waiting') {
      // Check if it's a 400 error with no_archive message (expected)
      const errorStatus = (error as any)?.status;
      const errorDetail = (error as any)?.detail || error.message || '';
      const isNoArchiveError =
        errorStatus === 400 || // 400 status means archive not ready yet
        errorDetail.toLowerCase().includes('no_archive') ||
        errorDetail.toLowerCase().includes('archive not found') ||
        errorDetail.toLowerCase().includes('not ready');

      if (isNoArchiveError) {
        // Continue waiting - this is expected, archive is not ready yet
        setArchiveState({ status: 'waiting' });
      } else {
        // Unexpected error (not 400, not no_archive related)
        setArchiveState({
          status: 'waiting',
          error: 'Failed to check archive status'
        });
      }
    }
  }, [error, archiveState.status]);

  // Timer for review period
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (reviewStartTime && archiveState.status === 'reviewing') {
      timer = setInterval(() => {
        const elapsed = Date.now() - reviewStartTime;
        const remaining = Math.max(0, REVIEW_TIME - elapsed);

        if (remaining === 0) {
          setArchiveState(prev => ({ ...prev, status: 'ready_to_verify', timeRemaining: 0 }));
        } else {
          setArchiveState(prev => ({ ...prev, timeRemaining: remaining }));
        }
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [reviewStartTime, archiveState.status]);

  // Actions
  const downloadArchive = useCallback(() => {
    if (archiveState.archiveUrl) {
      const link = document.createElement('a');
      link.href = archiveState.archiveUrl;
      link.click();

      setArchiveState(prev => ({ ...prev, status: 'downloaded' }));

      const now = Date.now();
      setReviewStartTime(now);
      setArchiveState(prev => ({
        ...prev,
        status: 'reviewing',
        timeRemaining: REVIEW_TIME
      }));
    }
  }, [archiveState.archiveUrl]);

  const acceptVerification = useCallback(() => {
    acceptMutation.mutate();
  }, [acceptMutation]);

  const declineVerification = useCallback(() => {
    declineMutation.mutate();
  }, [declineMutation]);

  // Reset state when verification status changes
  useEffect(() => {
    if (verificationStatus !== 'verification_needed') {
      setArchiveState({ status: 'completed' });
      setReviewStartTime(null);
      setHasCheckedInitially(false);
    } else if (verificationStatus === 'verification_needed') {
      // Reset to idle when verification is needed (if not already in a valid state)
      if (archiveState.status === 'completed' || (!hasCheckedInitially && archiveState.status !== 'idle')) {
        setArchiveState({ status: 'idle' });
        setHasCheckedInitially(false);
      }
    }
  }, [verificationStatus]);

  return {
    archiveState,
    downloadArchive,
    acceptVerification,
    declineVerification,
    isAccepting: acceptMutation.isPending,
    isDeclining: declineMutation.isPending,
  };
}
